import {Evaluator} from "@cm/graph-v3"
import {ImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {assertNever} from "@cm/utils"
import {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {ReplaceAWithB} from "@cm/utils/type"
import {
    AnyImageOpContext,
    AnyImageOpResult,
    ImageOpDefinition,
    ImageOpInputs,
    ImageOpInputsOrNodes,
    ImageOpNode,
    ImageOpOutputs,
    ImageOpParameters,
    SkipMappingTypes,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

export type ImageOpType = "hal" | "imgproc"

export type ImageOfType<M extends ImageOpType> = M extends "hal" ? AnyImageOpHal : ImageOpImgProc

export type ObjectOfType<T, Type extends ImageOpType> = ReplaceAWithB<T, AnyImageOpResult, ImageOfType<Type>, SkipMappingTypes> // add potential other classes which could be used in parameters here (to avoid the destruction of the class instance)
export type ImageOpInputsOfType<Definition extends ImageOpDefinition, Type extends ImageOpType> = ObjectOfType<ImageOpInputs<Definition>, Type>
export type ImageOpParametersOfType<Definition extends ImageOpDefinition, Type extends ImageOpType> = ObjectOfType<ImageOpParameters<Definition>, Type>
export type ImageOpOutputsOfType<Definition extends ImageOpDefinition, Type extends ImageOpType> = ObjectOfType<ImageOpOutputs<Definition>, Type>

export abstract class SpecializingImageOpNode<Definition extends ImageOpDefinition> extends ImageOpNode<Definition> {
    protected abstract toHal(
        evaluator: Evaluator<ImageOpContextHal>,
        parameters: ImageOpParametersOfType<Definition, "hal">,
    ): Promise<ImageOpOutputsOfType<Definition, "hal">>
    protected abstract toImgProc(
        evaluator: Evaluator<ImageOpContextImgProc>,
        parameters: ImageOpParametersOfType<Definition, "imgproc">,
    ): Promise<ImageOpOutputsOfType<Definition, "imgproc">>

    constructor(parameters: ImageOpInputsOrNodes<Definition>) {
        super(parameters)
    }

    override run = async (evaluator: Evaluator<AnyImageOpContext>): Promise<ImageOpOutputs<Definition>> => {
        this.checkIsDisposed()
        switch (evaluator.context.type) {
            case "hal":
                return (await this.toHal(
                    evaluator as Evaluator<ImageOpContextHal>,
                    this.parameters as ImageOpParametersOfType<Definition, "hal">,
                )) as ImageOpOutputs<Definition>
            case "imgproc":
                return (await this.toImgProc(
                    evaluator as Evaluator<ImageOpContextImgProc>,
                    this.parameters as ImageOpParametersOfType<Definition, "imgproc">,
                )) as ImageOpOutputs<Definition>
            default:
                assertNever(evaluator.context)
        }
    }
}
